import { createContext, useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";
import PageLoader from "../components/PageLoader";

type userType = "super-admin" | "admin" | "agent";
type statusType = "invite-sent" | "verified";

export type User = {
  id: number;
  name: string;
  code: string;
  email: string;
  phone: string;
  password: string;
  type: userType;
  status: statusType;
  createdAt?: string;
  updatedAt?: string;
}

type LoginUserInterface = {
  isLoggedIn: boolean;
  data: {
    token: string;
    user: User
  };
}

type ValueAuthContext = {
  user: User | undefined,
  isLoading: boolean;
  UpdateLoginData: (loginData: any) => void;
  HandleUserLogOut: () => void;
  pathName: string
};

const unProtectedPages = ["/auth", "/reset"]


export const AuthContext = createContext<ValueAuthContext | undefined>(undefined);

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<LoginUserInterface | undefined>()
  const [loading, setLoading] = useState(false)
  const navigation = useNavigate()
  const pathName = document.location.pathname

  const UpdateLoginData = (loginData: any) => {
    setUser(loginData)
  }

  // ---------------------------- [ IF NO SESSIONS ] ----------------------------
  const HandleNoSession = () => {
    if (!unProtectedPages?.includes(pathName) && !loading) {
      navigation("/auth")
      toast.error("Session Expired")
    }
    setUser(null)
    setLoading(false)
  }

  // ------------------------- [ UPDATE USER SESSION ] ------------------------
  const UpdateUserSession = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get("/auth/login")
      if (res.data?.isLoggedIn === false) {
        HandleNoSession()
      } else {
        setUser(res.data)
        setLoading(false)
      }
    } catch (error) {
      if (error?.response?.data?.isLoggedIn === false || !error?.response?.status) {
        HandleNoSession()
      }
      setLoading(false)
    }
  }


  useEffect(() => {
    UpdateUserSession()
  }, [pathName])


  // ----------------------------- [ USER LOGOUT ] ----------------------------------
  const HandleUserLogOut = async () => {
    setLoading(true)
    try {
      await axiosInstance.get("/auth/logout")
      toast.success("Logged out successfully")
      navigation("/auth")
      setLoading(false)
      setUser(null)
    } catch (error) {
      setLoading(false)
      toast.error(ParseErrors(error))
    }
  }

  // --------------------- [ IF USER LOGGED IN REDIRECT ] -------------------------

  useEffect(() => {
    if (!loading && user?.data?.user?.id && unProtectedPages?.includes(pathName)) {
      navigation("/")
    }

  }, [loading, user, navigation, pathName])


  // ------------------------------ [ SHOW LOADING SCREEN ] ------------------------------
  if (loading && !user?.isLoggedIn) {
    return <PageLoader />
  }

  return (
    <AuthContext.Provider value={{ pathName, UpdateLoginData, HandleUserLogOut, isLoading: loading, user: user?.data?.user as User }}>
      {children}
    </AuthContext.Provider>
  )
};

export default AuthProvider;
